// Cross-platform "save image to device" helper.
// - Web: triggers a normal browser download (anchor + download attr).
// - Android/iOS (Capacitor): writes the image bytes to the public Documents
//   directory via @capacitor/filesystem so the file ends up in the device's
//   Files/Photos app rather than getting lost inside the app sandbox.
//
// Why Documents and not the gallery directly: @capacitor/filesystem can write
// to Directory.Documents without any extra plugin or runtime permission on
// modern Android (scoped storage) and on iOS (Files app). Most gallery apps
// pick up files saved here, and the user can always open + share them from
// the Files app. Saving directly into the system Photos gallery requires
// additional plugins/permissions which add Play Store review overhead.
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";

/** Convert a Blob to a base64 string (no data URL prefix). */
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // result looks like "data:image/png;base64,XXXX" — strip the prefix.
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

export interface SaveResult {
  /** Where the file ended up — useful for showing the user a friendly toast. */
  location: "gallery" | "downloads" | "browser";
  /** Native filesystem URI when available (Capacitor only). */
  uri?: string;
}

/**
 * Save the image at `url` to the user's device.
 * Returns metadata about where it landed so the caller can show a toast like
 * "Saved to Photos" or "Image downloaded".
 */
export async function saveImageToDevice(
  url: string,
  filename: string,
): Promise<SaveResult> {
  // ── Native (Android/iOS via Capacitor) ─────────────────────────────────
  if (Capacitor.isNativePlatform()) {
    const res = await fetch(url);
    const blob = await res.blob();
    const base64 = await blobToBase64(blob);

    // Try public Documents first (visible in Files app + most galleries).
    // Some devices / OS versions reject Documents under scoped storage —
    // fall back to ExternalStorage (Downloads-like) and finally the app's
    // private Data dir so the user always gets *something* saved.
    const attempts: Array<{ directory: Directory; label: SaveResult["location"] }> = [
      { directory: Directory.Documents, label: "gallery" },
      { directory: Directory.External, label: "downloads" },
      { directory: Directory.Data, label: "downloads" },
    ];

    let lastErr: unknown;
    for (const attempt of attempts) {
      try {
        const written = await Filesystem.writeFile({
          path: `FitAura/${filename}`,
          data: base64,
          directory: attempt.directory,
          recursive: true,
        });
        return { location: attempt.label, uri: written.uri };
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr instanceof Error
      ? lastErr
      : new Error("Could not save image to device storage.");
  }

  // ── Web fallback: classic anchor download ──────────────────────────────
  const res = await fetch(url);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return { location: "browser" };
}
